import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InferType, object } from 'yup';

import {
  PortfolioImpactedCountModel,
  PostDataPortfolioApiArg,
  PostDataPortfolioValidateApiArg,
  useGetDataPortfolioQuery,
  usePostDataPortfolioMutation,
  usePostDataPortfolioValidateMutation,
} from '@client/api';
import {
  Button,
  combineStrings,
  FormField,
  FormSubmitButton,
  HelperText,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  Skeleton,
  useRouteDocumentTitle,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { CloseRequestModal } from '../../../components';
import { PortfolioPaths } from '../portfolio.router';

const SUPPORTED_FILES = ['CSV', 'XLS', 'XLSX'];

const schema = object({
  file: object().nullable(),
});

type FormValues = InferType<typeof schema>;

export function PortfolioImportRoute() {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const navigate = useNavigate();
  useRouteDocumentTitle(t('portfolioImport.title'), PortfolioPaths.PortfolioImport);

  const methods = useForm<FormValues>({
    defaultValues: { file: null },
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { isDirty, errors },
  } = methods;

  const file = watch('file');

  const [validateResponse, setValidateResponse] = useState<PortfolioImpactedCountModel | null>(null);

  const { data: templatePath } = useGetDataPortfolioQuery();

  const [validateData, { isLoading: isValidateLoading }] = usePostDataPortfolioValidateMutation();
  const [importData, { isLoading: isImportLoading }] = usePostDataPortfolioMutation();

  const isLoading = useMemo(() => isValidateLoading || isImportLoading, [isValidateLoading, isImportLoading]);

  const handleClose = useCallback(() => navigate(-1), [navigate]);

  const checkFile = useCallback(
    async (file?: File) => {
      clearErrors('file');
      setValidateResponse(null);

      if (!file) return;
      try {
        const payload = {
          body: new FormData(),
        };

        payload.body.append('payload', file);

        const response = await validateData(payload as unknown as PostDataPortfolioValidateApiArg).unwrap();

        setValidateResponse(response);
      } catch (e) {
        setError('file', { message: tCommon('invalidFormat') });
        return;
      }
    },
    [validateData, setError, clearErrors, tCommon],
  );

  const uploadFile = useCallback(
    async (file?: File) => {
      if (!file) return;

      try {
        const payload = {
          body: new FormData(),
        };

        payload.body.append('payload', file as unknown as Blob);

        await importData(payload as unknown as PostDataPortfolioApiArg).unwrap();
        setValidateResponse(null);

        handleClose();
      } catch (e) {
        return;
      }
    },
    [importData, handleClose],
  );

  // will be called automatically once file form value changes
  useEffect(() => {
    checkFile(file as unknown as File | undefined);
  }, [file, checkFile]);

  const supportedFilesText = useMemo(() => {
    const supportedFiles = SUPPORTED_FILES.map((x) => `.${x.toLowerCase()}`);
    return combineStrings(' ', tCommon('supportedFiles'), ...supportedFiles);
  }, [tCommon]);

  const [closingRequest, setClosingRequest] = useState<boolean>(false);

  const checkCloseRequest = () => {
    if (isDirty) {
      setClosingRequest(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmCloseRequest = () => {
    setClosingRequest(false);
    handleClose();
  };

  const handleCancelCloseRequest = () => {
    setClosingRequest(false);
  };

  return (
    <Modal
      open
      appear
      size="md"
      onClose={checkCloseRequest}
      onBackdropClick={checkCloseRequest}
      title={ready ? t('portfolioImport.title') : <Skeleton variant="text" />}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit((_) => uploadFile(file as unknown as File | undefined))}>
          <ModalContent className="p-4">
            <div className="mb-6">
              <FormField
                required
                name="file"
                label={tCommon('fileUpload')}
                render={({ field: { value, ...field } }) => (
                  <Input
                    {...field}
                    onChange={(e) => field.onChange(e.target.files?.[0])}
                    type="file"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    multiple={false}
                    submitting={field.submitting || isLoading}
                  />
                )}
              />
              <HelperText className={'mt-1'}>{supportedFilesText}</HelperText>
            </div>

            {!!validateResponse && (
              <>
                {ready ? (
                  <div className="h-5 text-sm">
                    {t('portfolioImport.add', {
                      entity: tGlobal('entity.building', { count: validateResponse.buildings.added }),
                      count: validateResponse.buildings.added,
                    })}
                  </div>
                ) : (
                  <Skeleton variant="text" />
                )}

                {ready ? (
                  <div className="h-5 text-sm">
                    {t('portfolioImport.update', {
                      entity: tGlobal('entity.building', { count: validateResponse.buildings.updated }),
                      count: validateResponse.buildings.updated,
                    })}
                  </div>
                ) : (
                  <Skeleton variant="text" />
                )}

                {ready ? (
                  <div className="h-5 text-sm">
                    {t('portfolioImport.add', {
                      entity: tGlobal('entity.commercialSpace', { count: validateResponse.commercialSpaces.added }),
                      count: validateResponse.commercialSpaces.added,
                    })}
                  </div>
                ) : (
                  <Skeleton variant="text" />
                )}

                {ready ? (
                  <div className="h-5 text-sm">
                    {t('portfolioImport.update', {
                      entity: tGlobal('entity.commercialSpace', { count: validateResponse.commercialSpaces.updated }),
                      count: validateResponse.commercialSpaces.updated,
                    })}
                  </div>
                ) : (
                  <Skeleton variant="text" />
                )}

                {ready ? (
                  <div className="h-5 text-sm">
                    {t('portfolioImport.add', {
                      entity: tGlobal('entity.residentialUnit', { count: validateResponse.residentialUnits.added }),
                      count: validateResponse.residentialUnits.added,
                    })}
                  </div>
                ) : (
                  <Skeleton variant="text" />
                )}

                {ready ? (
                  <div className="h-5 text-sm">
                    {t('portfolioImport.update', {
                      entity: tGlobal('entity.residentialUnit', { count: validateResponse.residentialUnits.updated }),
                      count: validateResponse.residentialUnits.updated,
                    })}
                  </div>
                ) : (
                  <Skeleton variant="text" />
                )}
              </>
            )}
          </ModalContent>

          <ModalFooter>
            <FormSubmitButton type="submit" disabled={!file || !!errors.file || isLoading}>
              {tCommon('import')}
            </FormSubmitButton>

            <a href={templatePath as string | undefined} download>
              <Button color="primary" disabled={!templatePath || isLoading}>
                {tCommon('downloadEntity', { entity: 'template' })}
              </Button>
            </a>

            <Button color="primary-dark" onClick={checkCloseRequest}>
              {tCommon('close')}
            </Button>
          </ModalFooter>

          <CloseRequestModal
            open={closingRequest}
            onConfirm={handleConfirmCloseRequest}
            onCancel={handleCancelCloseRequest}
          />
        </form>
      </FormProvider>
    </Modal>
  );
}
